import { ColumnFilter, Table } from '@tanstack/react-table';
import { isEqual } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FullUser } from '@work4all/models/lib/Classes/FullUser.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { useLicenseLockAlert } from '../../../mutex/useLicenseLockAlert';
import { settings, useSetting } from '../../../settings';
import { OrganisationArea } from '../../organisation-area';
import { EntityTableV8, IEntityTableV8 } from '../entity-table/EntityTableV8';
import { useEntityDataTableV8 } from '../entity-table/use-entity-data-table-v8';
import schema from '../schemata/fullUser-table-schema.json';

import { useUserActions } from './use-user-actions';

const defaultSort = [
  { field: 'displayName', direction: SortDirection.ASCENDING },
];

const forceRequestFields: FullUser = {
  groupId: null,
  state: null,
  location: null,
  street: null,
  postalCode: null,
  salutation: {
    id: null,
    name: null,
    standardletterSalutation: null,
  },
};

export function UsersTable(props: IEntityTableV8) {
  const { t } = useTranslation();

  const dataTable = useEntityDataTableV8<FullUser, never>({
    ...props,
    schema: schema as never,
    defaultSort,
    forceRequestFields,
    rowModifiers: (value) => {
      return {
        isFaded: value.hasLeft,
      };
    },
    defaultColumnFilters: useUserTableDefaultColumnFilters(),
  });

  useSaveLastUserKindFilter(dataTable.table);

  const actions = useUserActions({ table: dataTable.table });

  useLicenseLockAlert();

  return (
    <EntityTableV8
      {...props}
      {...dataTable}
      areas={{
        left: {
          title: t('COMMON.ORGANISATION'),
          content: (
            <OrganisationArea
              entity={Entities.fullUser}
              table={dataTable.table}
            />
          ),
          resizable: true,
          collapsible: true,
          custom: true,
        },
      }}
      actions={actions}
    />
  );
}

function useUserTableDefaultColumnFilters(): ColumnFilter[] {
  const { t } = useTranslation();

  const lastFilter = useSetting(settings.lastFilter(Entities.fullUser));

  const defaultColumnFilters = useMemo<ColumnFilter[]>(() => {
    const inactiveFilter: ColumnFilter = {
      id: 'hasLeft',
      value: {
        value: [{ id: false, name: t('BOOLEAN_FILTER.false') }],
        filterType: 'boolean',
      },
    };

    if (!lastFilter.value) return [inactiveFilter];

    return [inactiveFilter, ...lastFilter.value];
  }, [lastFilter.value, t]);

  return defaultColumnFilters;
}

function useSaveLastUserKindFilter(table: Table<unknown>): void {
  const lastFilter = useSetting(settings.lastFilter(Entities.fullUser));

  const columnFilters = table.getState().columnFilters;

  useEffect(() => {
    const userKindFilter = columnFilters.find((filter) => {
      return filter.id === 'userKind';
    });

    if (!userKindFilter || isEqual(lastFilter.value, [userKindFilter])) {
      return;
    }

    lastFilter.set([userKindFilter as any]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters]);
}
